<template>
  <div 
    ref="gradientContainer"
    :style="backgroundStyles"
  >
    <!-- Texture overlay div -->
    <div 
      v-if="textureVariant !== 'none'"
      :style="textureStyles"
    ></div>
    
    <!-- Content slot -->
    <div :style="contentStyles">
      <slot></slot>
    </div>
  </div>
</template>

<script>
// Import texture files directly
import rainbowTexture from '../assets/Rainbow_Texture.png';
import blackTexture from '../assets/Black_Texture.png';
import imageTexture from '../assets/Image_Texture.png';
import whiteTexture from '../assets/White_Texture.png';
import washedTexture from '../assets/Washed_Texture.png';
import copyTexture from '../assets/Copy_Texture.png';

export default {
  name: 'GradientBackground',
  props: {
    gradientTop: {
      type: String,
      default: '#1a1a1a'
    },
    gradientBottom: {
      type: String,
      default: '#ffffff'
    },
    textureVariant: {
      type: String,
      default: 'rainbow',
      validator: (value) => ['rainbow', 'black', 'image', 'white', 'washed', 'copy', 'none'].includes(value)
    },
    textureOpacity: {
      type: Number,
      default: 0.6
    }
  },
  computed: {
    backgroundStyles() {
      return {
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflowY: 'auto',
        padding: '1rem',
        paddingBottom: '4rem',
        background: `linear-gradient(to bottom, ${this.gradientTop}, ${this.gradientBottom})`,
        // Custom scrollbar styles
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(255, 255, 255, 0.3) transparent'
      };
    },
    
    textureStyles() {
      const textureMap = {
        rainbow: rainbowTexture,
        black: blackTexture,
        image: imageTexture,
        white: whiteTexture,
        washed: washedTexture,
        copy: copyTexture
      };
      
      const blendModeMap = {
        rainbow: 'overlay',
        black: 'soft-light',
        image: 'overlay',
        white: 'screen',
        washed: 'soft-light',
        copy: 'overlay'
      };
      
      console.log('Using texture:', this.textureVariant, 'URL:', textureMap[this.textureVariant]);
      
      return {
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        backgroundImage: `url('${textureMap[this.textureVariant]}')`,
        backgroundSize: '500px',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'top',
        opacity: this.textureOpacity.toString(),
        mixBlendMode: blendModeMap[this.textureVariant],
        pointerEvents: 'none',
        zIndex: '1',
        // Gradient mask effect
        WebkitMask: 'linear-gradient(to bottom, black 0%, black 70%, transparent 100%)',
        mask: 'linear-gradient(to bottom, black 0%, black 70%, transparent 100%)'
      };
    },

    contentStyles() {
      return {
        position: 'relative',
        zIndex: '10',
        width: '100%',
        height: '100%'
      };
    }
  },
  
  mounted() {
    // Apply webkit scrollbar styles via JavaScript since CSS doesn't work in Shadow DOM
    this.applyScrollbarStyles();
  },
  
  methods: {
    applyScrollbarStyles() {
      const container = this.$refs.gradientContainer;
      if (container) {
        // Create unique instance ID
        const instanceId = Math.random().toString(36).substr(2, 9);
        const className = `gradient-container-${instanceId}`;
        
        // Add class to container
        container.classList.add(className);
        
        // Create and inject scrollbar styles
        const style = document.createElement('style');
        style.textContent = `
          .${className}::-webkit-scrollbar {
            width: 6px;
          }
          .${className}::-webkit-scrollbar-track {
            background: transparent;
          }
          .${className}::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.3);
            border-radius: 3px;
            border: none;
          }
          .${className}::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.5);
          }
        `;
        
        // Inject into document head
        document.head.appendChild(style);
        
        // Store reference to remove on unmount
        this.injectedStyle = style;
      }
    }
  },
  
  beforeUnmount() {
    // Clean up injected styles
    if (this.injectedStyle && this.injectedStyle.parentNode) {
      this.injectedStyle.parentNode.removeChild(this.injectedStyle);
    }
  }
}
</script>